import type { VideoModel } from "@ziggapp/klap-core/video";
import { PublicationModel } from "../../types/models";
import { toValidDate } from "../../utils/date";
import { isImmutablePub } from "./engine/status";
import {
  PublicationChangeSet,
  PublicationOperationCandidate,
  PublicationUpdateCandidate,
  VideoPublicationState,
} from "./types";

type BuildPublicationChangeSetArgs = {
  orderedVideos: Pick<VideoModel, "id">[];
  perVideoStateById: Record<string, VideoPublicationState>;
  /** The latest known server snapshot. Unlike an account-keyed map, this keeps
   * every persisted row when historical posts share a video/account. */
  baselinePublications?: readonly PublicationModel[];
  existingPublicationByVideoAndAccount?: Map<string, PublicationModel>;
  editableVideoScopeIdSet: Set<string>;
  isSingleVideoPublishNow: boolean;
  areOptionalDatesEqualToMinute: (
    leftDate?: Date | null,
    rightDate?: Date | null,
  ) => boolean;
  areDeepValuesEqual: (leftValue: unknown, rightValue: unknown) => boolean;
};

export type SelectPublicationsToDeleteArgs = Pick<
  BuildPublicationChangeSetArgs,
  | "existingPublicationByVideoAndAccount"
  | "baselinePublications"
  | "perVideoStateById"
  | "editableVideoScopeIdSet"
> & { orderedVideoIds: string[] };

/**
 * The persisted posts missing from the working session. Account selection
 * is never a deletion predicate: saved ownership and legacy duplicates
 * remain intact unless their actual persisted row was removed. The calendar
 * uses this same selector, so its preview and Save hide the same posts.
 */
export const selectPublicationsToDelete = ({
  existingPublicationByVideoAndAccount,
  baselinePublications,
  perVideoStateById,
  editableVideoScopeIdSet,
  orderedVideoIds,
}: SelectPublicationsToDeleteArgs): PublicationModel[] => {
  const orderedVideoIdSet = new Set(orderedVideoIds);
  const baseline = baselinePublications ??
    [...(existingPublicationByVideoAndAccount?.values() ?? [])];
  const seenIds = new Set<string>();
  return baseline.filter(
    (publication) => {
      if (seenIds.has(publication.id)) return false;
      seenIds.add(publication.id);
      if (!publication.projectId || !publication.accountId) return false;
      if (isImmutablePub(publication)) return false;
      if (!editableVideoScopeIdSet.has(publication.projectId)) return false;
      if (!orderedVideoIdSet.has(publication.projectId)) return true;

      return !(perVideoStateById[publication.projectId]?.localPublications ?? [])
        .some((local) => local.id === publication.id &&
          local.accountId === publication.accountId);
    },
  );
};

export const buildPublicationChangeSet = ({
  orderedVideos,
  perVideoStateById,
  existingPublicationByVideoAndAccount,
  baselinePublications,
  editableVideoScopeIdSet,
  isSingleVideoPublishNow,
  areOptionalDatesEqualToMinute,
  areDeepValuesEqual,
}: BuildPublicationChangeSetArgs): PublicationChangeSet => {
  const baseline = baselinePublications ??
    [...(existingPublicationByVideoAndAccount?.values() ?? [])];
  const baselineById = new Map(baseline.map((pub) => [pub.id, pub]));
  const processedIds = new Set<string>();
  // Publish-now dates only new posts. Saved context keeps its staged date.
  // Ordinary Save consumes the staged timestamp and invents no dates.
  const publishNowDate = isSingleVideoPublishNow ? new Date() : undefined;
  const publicationsToProcess = orderedVideos.flatMap((video) => {
    const videoState = perVideoStateById[video.id];
    if (!videoState) return [];
    return videoState.localPublications.flatMap((publication) => {
      const baselinePub = baselineById.get(publication.id);
      if (processedIds.has(publication.id) || isImmutablePub(publication) ||
        (baselinePub && isImmutablePub(baselinePub))) {
        return [];
      }
      processedIds.add(publication.id);

      return [
        {
          publication,
          projectId: video.id,
          accountId: publication.accountId,
          scheduledAt: toValidDate(publication.scheduledAt) ?? undefined,
        },
      ];
    });
  });

  const publicationsToDelete = selectPublicationsToDelete({
    existingPublicationByVideoAndAccount,
    baselinePublications: baseline,
    perVideoStateById,
    editableVideoScopeIdSet,
    orderedVideoIds: orderedVideos.map((video) => video.id),
  });

  const publicationsToCreate: PublicationOperationCandidate[] = [];
  const publicationsToUpdate: PublicationUpdateCandidate[] = [];

  publicationsToProcess.forEach((publicationToProcess) => {
    const persistedPublication = baselineById.get(publicationToProcess.publication.id);
    if (!persistedPublication ||
      persistedPublication.projectId !== publicationToProcess.projectId ||
      persistedPublication.accountId !== publicationToProcess.accountId) {
      publicationsToCreate.push({
        ...publicationToProcess,
        scheduledAt: publishNowDate ?? publicationToProcess.scheduledAt,
      });
      return;
    }
    const isScheduleEdited = !areOptionalDatesEqualToMinute(
      publicationToProcess.scheduledAt,
      toValidDate(persistedPublication.scheduledAt),
    );
    const isPayloadEdited = !areDeepValuesEqual(
      publicationToProcess.publication.postInfo,
      persistedPublication.postInfo,
    );
    const isRetry = persistedPublication.status === "failed" &&
      publicationToProcess.publication.status !== "failed";
    if (!isScheduleEdited && !isPayloadEdited && !isRetry) return;

    publicationsToUpdate.push({
      ...publicationToProcess,
      persistedPublication,
    });
  });

  const editedCount = publicationsToUpdate.length;
  const createdCount = publicationsToCreate.length;
  const removedCount = publicationsToDelete.length;

  return {
    publicationsToCreate,
    publicationsToUpdate,
    publicationsToDelete,
    editedCount,
    createdCount,
    removedCount,
    totalCount: editedCount + createdCount + removedCount,
  };
};

export const formatEditConfirmationChangeSummary = ({
  editedCount,
  createdCount,
  removedCount,
}: Pick<
  PublicationChangeSet,
  "editedCount" | "createdCount" | "removedCount"
>) => {
  const summaryParts: string[] = [];
  if (createdCount > 0) {
    summaryParts.push(`${createdCount} created`);
  }
  if (editedCount > 0) {
    summaryParts.push(`${editedCount} updated`);
  }
  if (removedCount > 0) {
    summaryParts.push(`${removedCount} removed`);
  }
  if (summaryParts.length === 0) return null;
  return summaryParts.join(", ");
};

export const getEditConfirmationFirstAffectedScheduleDate = (
  publicationChanges: PublicationChangeSet,
  areOptionalDatesEqualToMinute: (
    leftDate?: Date | null,
    rightDate?: Date | null,
  ) => boolean,
) => {
  const affectedScheduleDates: Date[] = [];

  publicationChanges.publicationsToCreate.forEach((publicationToCreate) => {
    const scheduledDate = toValidDate(publicationToCreate.scheduledAt);
    if (scheduledDate) affectedScheduleDates.push(scheduledDate);
  });

  publicationChanges.publicationsToDelete.forEach((publicationToDelete) => {
    const scheduledDate = toValidDate(publicationToDelete.scheduledAt);
    if (scheduledDate) affectedScheduleDates.push(scheduledDate);
  });

  publicationChanges.publicationsToUpdate.forEach((publicationToUpdate) => {
    const persistedScheduleDate = toValidDate(
      publicationToUpdate.persistedPublication.scheduledAt,
    );
    const nextScheduleDate = toValidDate(publicationToUpdate.scheduledAt);
    const isScheduleEdited = !areOptionalDatesEqualToMinute(
      nextScheduleDate,
      persistedScheduleDate,
    );
    if (!isScheduleEdited) return;
    if (nextScheduleDate) {
      affectedScheduleDates.push(nextScheduleDate);
      return;
    }
    if (persistedScheduleDate) {
      affectedScheduleDates.push(persistedScheduleDate);
    }
  });

  if (!affectedScheduleDates.length) return null;

  return affectedScheduleDates.reduce((earliestDate, currentDate) => {
    return currentDate.getTime() < earliestDate.getTime()
      ? currentDate
      : earliestDate;
  });
};
